import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';

const RANGES = [
  { days: 1, label: '24h' },
  { days: 3, label: '3 days' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
];

const TYPES = [
  { value: '', label: 'All' },
  { value: 'feature_request', label: 'Feature requests' },
  { value: 'issue', label: 'Issues' },
  { value: 'new', label: 'Not yet clustered' },
];

const LANG_NAMES = {
  en: 'English', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian', pt: 'Portuguese',
  nl: 'Dutch', zh: 'Chinese', ja: 'Japanese', ko: 'Korean', ru: 'Russian', pl: 'Polish',
};

function dayLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 999, cursor: 'pointer',
        border: `1px solid ${active ? 'var(--coros-red)' : 'var(--border-color)'}`,
        background: active ? 'var(--coros-red)' : 'var(--bg-elevated)',
        color: active ? '#fff' : 'var(--text-secondary)',
      }}
    >
      {children}
    </button>
  );
}

function FeedRow({ item }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      try {
        const res = await api.ticket(item.id);
        setDetail(res.data);
      } catch (e) {
        setDetail({ error: e.message });
      } finally {
        setLoading(false);
      }
    }
  };

  const typeColor = item.cluster?.type === 'issue' ? 'var(--color-warning)' : 'var(--text-muted)';

  return (
    <div style={{ borderBottom: '1px solid var(--border-color)' }}>
      <button
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'baseline', gap: 10, width: '100%', textAlign: 'left',
          padding: '9px 4px', background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 11, width: 10, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text-primary)',
          fontStyle: item.summarized ? 'normal' : 'italic',
        }}>
          {item.summary}
        </span>
        {item.language && item.language !== 'en' && (
          <span title={LANG_NAMES[item.language] || item.language} style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)',
            border: '1px solid var(--border-color)', borderRadius: 4, padding: '0 4px', flexShrink: 0,
          }}>
            {item.language}
          </span>
        )}
        {item.cluster?.type === 'issue' && (
          <span style={{ fontSize: 10, fontWeight: 700, color: typeColor, flexShrink: 0 }}>ISSUE</span>
        )}
      </button>

      {open && (
        <div style={{ padding: '4px 4px 14px 24px', fontSize: 13 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>
            <span>{new Date(item.created_at).toLocaleString()}</span>
            {item.device_model && <span>{item.device_model}</span>}
            {item.country && <span>{item.country}</span>}
            {item.cluster ? (
              <span>
                Cluster:{' '}
                <Link to={`/clusters/${item.cluster.id}`} style={{ color: 'var(--coros-red)', fontWeight: 600, textDecoration: 'none' }}>
                  {item.cluster.name}
                </Link>
              </span>
            ) : <span>Not clustered yet (clustering runs weekly)</span>}
            <a href={item.zendesk_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--coros-red)', fontWeight: 600 }}>
              Open #{item.zendesk_id} in Zendesk ↗
            </a>
          </div>

          {loading && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
          {detail?.error && <p className="text-coros-status-error">{detail.error}</p>}
          {detail && !detail.error && (
            <>
              {detail.subject && <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>{detail.subject}</p>}
              <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.55 }}>
                {detail.description_english || detail.description}
              </p>
              {detail.description_english && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>
                    Original ({LANG_NAMES[detail.original_language] || detail.original_language})
                  </summary>
                  <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text-muted)', marginTop: 6 }}>{detail.description}</p>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function Feed() {
  const [days, setDays] = useState(7);
  const [type, setType] = useState('');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, summarized: 0, hasMore: false, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.feed({ days, type, q, page, limit: 100 });
      const d = res.data;
      setItems((prev) => (page === 1 ? d.items : [...prev, ...d.items]));
      setMeta({ total: d.total, summarized: d.summarized, hasMore: d.hasMore, page: d.page });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, type, q]);

  useEffect(() => { load(1); }, [load]);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setQ(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  let lastDay = null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div style={{ marginBottom: 16 }}>
        <h1 className="text-2xl font-bold text-coros-text-primary">Feed</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          Every feedback ticket in one line, translated to English, newest first. Click a line for the full text and Zendesk link.
        </p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        {RANGES.map((r) => (
          <Chip key={r.days} active={days === r.days} onClick={() => setDays(r.days)}>{r.label}</Chip>
        ))}
        <span style={{ width: 1, height: 18, background: 'var(--border-color)', margin: '0 4px' }} />
        {TYPES.map((t) => (
          <Chip key={t.value} active={type === t.value} onClick={() => setType(t.value)}>{t.label}</Chip>
        ))}
      </div>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter summaries (e.g. watch face, Strava, swim)"
        style={{
          width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 8, marginBottom: 12,
          border: '1px solid var(--border-color)', background: 'var(--bg-elevated)', color: 'var(--text-primary)',
        }}
      />

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        {meta.total} ticket{meta.total === 1 ? '' : 's'}
        {meta.summarized < meta.total && ` · ${meta.total - meta.summarized} awaiting summary (shown by subject)`}
      </p>

      {error && <p className="text-coros-status-error">{error}</p>}

      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '0 12px' }}>
        {items.map((item) => {
          const label = dayLabel(item.created_at);
          const header = label !== lastDay ? label : null;
          lastDay = label;
          return (
            <React.Fragment key={item.id}>
              {header && (
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase',
                  color: 'var(--text-muted)', padding: '14px 4px 4px',
                }}>
                  {header}
                </div>
              )}
              <FeedRow item={item} />
            </React.Fragment>
          );
        })}
        {!loading && items.length === 0 && !error && (
          <p style={{ color: 'var(--text-muted)', padding: '24px 4px', textAlign: 'center' }}>No tickets match.</p>
        )}
        {loading && <p style={{ color: 'var(--text-muted)', padding: '16px 4px', textAlign: 'center' }}>Loading…</p>}
      </div>

      {meta.hasMore && !loading && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button className="btn-secondary" onClick={() => load(meta.page + 1)}>Load more</button>
        </div>
      )}
    </div>
  );
}

export default Feed;
